// src/Dashboard.jsx
import React from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { Box, Typography, CircularProgress, Alert } from '@mui/material';
import NavBar from './components/NavBar';
import theme from './theme';
import DashboardHeader from './components/DashboardHeader';
import TilesShowcase from './components/tiles/TilesShowcase';
import { useDashboardData } from './context/DashboardDataContext';
import { ReceiptsVisibilityProvider } from './context/ReceiptsVisibilityContext';

// dev scratch (optional)
import { slugify, jtbdKeyFromFilename } from './utils/normalize';
console.log(slugify('I want to Identify Profitable Properties'));
console.log(jtbdKeyFromFilename('i-want-to-identify-profitable-properties__satisfaction.json'));

/* ---------- Small body that shows loading/error; must be inside provider ---------- */
function DashboardBody() {
  const { ready, error } = useDashboardData();
  return (
    <>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load dashboard data. Check console.
        </Alert>
      )}
      {!ready && !error && <CircularProgress size={20} />}
    </>
  );
}

/* ---------- Bridge that supplies real options from indices ---------- */
function DashboardHeaderBridge({
  mode,
  setMode,
  selected,
  setSelected,
  headerSummary,
  dateRange,
  setDateRange,
  showReceipts,
  setShowReceipts,
  datasetOptions,
  selectedDatasets,
  setSelectedDatasets,
}) {
  const { ready, indices, getProductSections, getJobSections } = useDashboardData();

  // Build options list + counts, and sort by highest count first
  const { options, optionCounts } = React.useMemo(() => {
    if (!ready) {
      return { options: [], optionCounts: {} };
    }

    const baseList =
      mode === 'product'
        ? indices?.product_list || []
        : indices?.jtbd_list || [];

    const withCounts = baseList.map((name) => {
      let count = 0;
      try {
        const { sections } =
          mode === 'product' ? getProductSections(name) : getJobSections(name);
        count = sections?.[0]?.section_total_interviews ?? 0;
      } catch (e) {
        count = 0;
      }
      return { name, count };
    });

    withCounts.sort((a, b) => {
      if (b.count !== a.count) return b.count - a.count;
      return a.name.localeCompare(b.name);
    });

    const sortedNames = withCounts.map((item) => item.name);
    const countsMap = withCounts.reduce((acc, item) => {
      acc[item.name] = item.count;
      return acc;
    }, {});

    return { options: sortedNames, optionCounts: countsMap };
  }, [ready, mode, indices, getProductSections, getJobSections, selectedDatasets]);

  // ensure selected is valid for current mode; if not, pick first option
  React.useEffect(() => {
    if (!ready) return;
    if (!options?.length) return;
    if (!selected || !options.includes(selected)) {
      setSelected(options[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, mode, options]);

  return (
    <DashboardHeader
      mode={mode}
      onModeChange={setMode}
      options={options}
      optionCounts={optionCounts}
      selected={selected}
      onSelectedChange={setSelected}
      headerSummary={headerSummary}
      dateRange={dateRange}
      onDateRangeChange={setDateRange}
      showReceipts={showReceipts}
      onShowReceiptsChange={setShowReceipts}
      datasetOptions={datasetOptions}
      selectedDatasets={selectedDatasets}
      onSelectedDatasetsChange={setSelectedDatasets}
    />
  );
}

/* ---------- All logic that uses the data context lives here (inside provider) ---------- */
function DashboardInner() {
  const [mode, setMode] = React.useState('product'); // 'product' | 'job'
  const [selected, setSelected] = React.useState('');
  const [headerSummary, setHeaderSummary] = React.useState(null);
  const [dateRange, setDateRange] = React.useState('all'); // UI only for now
  const [showReceipts, setShowReceipts] = React.useState(false);

  // Access selectors & readiness from the dataset context (now safe)
  const {
    ready,
    getProductSections,
    getJobSections,
    datasetOptions,
    selectedDatasets,
    setSelectedDatasets,
  } = useDashboardData();

  // Derive the header summary purely from selectors (no dummy endpoint)
  React.useEffect(() => {
    if (!ready || !selected) return;

    try {
      const { sections } =
        mode === 'product' ? getProductSections(selected) : getJobSections(selected);

      // section_total_interviews is consistent across the sections we build;
      // read it from the first section defensively.
      const interviewCount = sections?.[0]?.section_total_interviews ?? 0;

      setHeaderSummary({
        mode,
        selected,
        interview_count: interviewCount,
        // keep "what changed" placeholder text you already show in the header
        changes_note: 'What changed vs last cycle? (4 months): no changes to show yet.',
        interviews_link: '#interviews', // harmless placeholder
      });
    } catch (e) {
      console.debug('[Dashboard] Failed to derive header summary from selectors:', e);
      setHeaderSummary({
        mode,
        selected,
        interview_count: 0,
        changes_note: 'What changed vs last cycle? (4 months): no changes to show yet.',
        interviews_link: '#interviews',
      });
    }
  }, [ready, mode, selected, getProductSections, getJobSections]);

  // (optional) debug log
  React.useEffect(() => {
    if (ready && mode === 'product' && selected) {
      const { sections } = getProductSections(selected);
      console.log('Product sections (preview)', sections);
    }
  }, [ready, mode, selected, getProductSections]);

  return (
    <Box sx={{ padding: 3 }}>
      <Typography variant="h4" gutterBottom>
        Dashboard
      </Typography>

      {/* readiness indicator for the dataset bootstrap */}
      <DashboardBody />

      <Box my={4}>
        {/* no spinner here anymore since we dropped the dummy header fetch */}
        <DashboardHeaderBridge
          mode={mode}
          setMode={setMode}
          selected={selected}
          setSelected={setSelected}
          headerSummary={headerSummary}
          dateRange={dateRange}
          setDateRange={setDateRange}
          showReceipts={showReceipts}
          setShowReceipts={setShowReceipts}
          datasetOptions={datasetOptions}
          selectedDatasets={selectedDatasets}
          setSelectedDatasets={setSelectedDatasets}
        />
      </Box>

      {/* Sections (now driven by real selectors) */}
      <ReceiptsVisibilityProvider value={showReceipts}>
        <TilesShowcase mode={mode} selected={selected} />
      </ReceiptsVisibilityProvider>
    </Box>
  );
}

/* ---------- Top-level component no longer uses the data hook ---------- */
export default function Dashboard() {
  return (
    <ThemeProvider theme={theme}>
      <NavBar />
      <DashboardInner />
    </ThemeProvider>
  );
}
